import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { getDeviceInfo } from '@/utils/device-detection';
import { getIpInfo } from '@/utils/ip-detection';
import { BASE_URL, SITE_URL } from '@/config/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQueryClient } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { useAccountStore } from '@/stores/useAccountStore';

const authChannel = new BroadcastChannel('auth');


interface Subscription {
  id: string;
  object: string;
  current_period_end: number;
  current_period_start: number;
  plan: {
    id: string;
    object: string;
    active: boolean;
    amount: number;
    amount_decimal: string;
    billing_scheme: string;
    created: number;
    currency: string;
    interval: string;
    interval_count: number;
    livemode: boolean;
    nickname: string | null;
    product: string;
    trial_period_days: number | null;
    usage_type: string;
  };
  quantity: number;
  items: {
    data: Array<{
      id: string;
      object: string;
      plan: {
        nickname: string | null;
      };
    }>;
  };
}

interface Invoice {
  id: string;
  amount_due: number;
  amount_paid: number;
  amount_remaining: number;
  created: number;
  currency: string;
  customer_email: string;
  customer_name: string;
  period_end: number;
  period_start: number;
  status: string;
  hosted_invoice_url: string;
  invoice_pdf: string;
  subscription: string;
  lines: {
    data: Array<{
      id: string;
      amount: number;
      description: string;
      plan: {
        nickname: string | null;
      };
      period: {
        start: number;
        end: number;
      };
    }>;
  };
}

interface User {
  id: string;
  userId: string;
  username: string;
  email: string;
  referral: string;
  schoolJoinCode: string;
  hasCreatedEvent: boolean;
  hasCreatedCategory: boolean;
  hasCreatedStudent: boolean;
  hasCreatedTeacher: boolean;
  is_wizard_disable: boolean;
  schools: any[];
  is_firstlogin: boolean;
  currency_code: string;
  name: string;
  notificationSettings: {
    email_new_bookings?: boolean;
    email_lesson_reminders?: boolean;
    email_schedule_changes?: boolean;
    email_marketing_updates?: boolean;
    email_student_off?: boolean;
    email_teacher_off?: boolean;
    push_new_bookings?: boolean;
    push_lesson_reminders?: boolean;
    push_schedule_changes?: boolean;
    push_student_off?: boolean;
    push_teacher_off?: boolean;
  };
  school_id: string;
  timezone: string;
  school_country_code:string;
  firstname: string;
  lastname: string;
  roleType: string;
  provider: string;
  account_checked: number;
  code_verification:string;
  subscriptions: Subscription[];
  invoices: Invoice[];
  is_subscribed:boolean,
  is_trialing:boolean,
  trial_ends_at:any,
  paymentMethods: any[];
  stripe_user_method_payment:any;
  teacher:any;
  prices:any;
  school_name:string;
  phone:string;
  mobile: string;
  address: string;
  street_number: string;
  street: string;
  place: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  parent_name_1: string;
  parent_name_2: string;
  mother_email: string;
  father_email: string;
  mother_phone: string;
  father_phone: string;
  bio: string;
  image: string;
  image_url: string;
  account_type: string;
  is_active: number;
  students: any[];
  categories: any[];
  locations: any[];
  taxes: any[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingTwoFactor: boolean;
  pendingTwoFactorProvider: 'email' | 'google' | 'apple' | null;
  pendingTwoFactorToken: string | null;
  clearPendingTwoFactor: () => void;
  resetTwoFactor: () => void;
  login: (username: string, password: string, otpCode?: string) => Promise<'success' | '2fa_required'>;
  register: (
    firstName: string, 
    lastName: string,
    username: string, 
    email: string, 
    password: string,
    ipAddress?: string,
    country?: string,
    deviceInfo?: string,
    timezone?: string,
    accountType?: string,
    studentToken?: string,
    schoolName?: string,
    referral?: string,
  ) => Promise<any>;
  loginWithGoogle: (token: string, otpCode?: string) => Promise<'success' | '2fa_required'>;
  loginWithGoogleCode: (code: string) => Promise<'success' | '2fa_required'>;
  loginWithApple: (token: string, otpCode?: string) => Promise<'success' | '2fa_required'>;
  loginWithAppleCode: (code: string) => Promise<'success' | '2fa_required'>;
  logout: () => void;
  logoutToken: (tokenParam) => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingTwoFactor, setPendingTwoFactor] = useState(false);
  const [pendingTwoFactorProvider, setPendingTwoFactorProvider] = useState<'email' | 'google' | 'apple' | null>(null);
  const [pendingTwoFactorToken, setPendingTwoFactorToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  useEffect(() => {
    const storedToken = localStorage.getItem('auth-token');
    if (storedToken) {
      setToken(storedToken);
      fetchUserData(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    authChannel.onmessage = (event) => {
      if (event.data === 'FORCE_LOGOUT') {
        // nettoyage local
        localStorage.removeItem('auth-token');
        eventService.clearEventsCache();
        queryClient.clear();

        setToken(null);
        setUser(null);

        navigate('/login', { replace: true });
      }
    };

    return () => {
      authChannel.close();
    };
  }, []);

  const fetchUserData = async (authToken: string) => {
    try {
      const response = await fetch(`${BASE_URL}/api/user`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        //console.log(userData);
        // Sync to account switcher
        useAccountStore.getState().syncCurrentUser(userData, authToken);
        
        return userData;
      } else {
        localStorage.removeItem('auth-token');
        setToken(null);
      }
    } catch (error) {
      localStorage.removeItem('auth-token');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserData = async () => {
    if (token) {
      try {
        const response = await fetch(`${BASE_URL}/api/user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
            
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          //console.log(userData);
          return userData;
        } else {
          throw new Error('Failed to refresh user data');
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        throw error;
      }
    }
  };

  const login = async (
    username: string,
    password: string,
    otpCode?: string,
  ): Promise<'success' | '2fa_required'> => {
    setIsLoading(true);
    
    try {
      const ipInfo = await getIpInfo();
      const deviceInfo = getDeviceInfo();
      
      const response = await fetch(`${BASE_URL}/api/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify({ 
            username, 
            password,
            ...(otpCode ? { code: otpCode } : {}),
            ip_address: ipInfo.ip,
            country: ipInfo.countryCode,
            device_info: JSON.stringify(deviceInfo)
        }),
    });
      
      let data: any = {};
      let responseText = '';

      try {
        responseText = await response.text();
      } catch {
        responseText = '';
      }

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          data = { message: responseText };
        }
      }

      const message = typeof data?.message === 'string' ? data.message : '';
      const requiresTwoFactor =
        data?.two_factor_required === true ||
        message === 'two_factor_required' ||
        message.includes('two_factor');

      // console.log('Login response:', { response, data, message, requiresTwoFactor });

      // Le backend demande le code 2FA avant de délivrer le token
      if (requiresTwoFactor) {
        setPendingTwoFactor(true);
        setPendingTwoFactorProvider('email');
        setPendingTwoFactorToken(null);
        return '2fa_required';
      }
      
      if (response.ok && data.message === 'success') { 
        localStorage.setItem('auth-token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        localStorage.setItem('2fa-prompt-pending', '1');
        
        window.scrollTo(0, 0);

        //reset all localstorage wizard
        localStorage.removeItem('wizard-started');
        localStorage.removeItem('wizard-step');
        localStorage.removeItem('wizard-active');
        localStorage.removeItem('wizard-welcome-dismissed');
        localStorage.removeItem('wizard-completed');
        window.dispatchEvent(new Event('wizard-state-changed'));

        toast({
          title: t('login.loginSuccess'),
          description: t('login.title'),
          variant: "default"
        });

        const storedToken = localStorage.getItem('auth-token');
        await fetchUserData(storedToken);

        navigate('/agenda', { replace: true });
        return 'success';
      }
      if (!response.ok || data.message !== 'success') {
        toast({
          variant: 'destructive',
          title: t('login.loginFailed'),
          description: t('login.pleaseTryAgain'),
        });
        throw new Error(data.message || 'Login failed');
      } 
    } catch (error) {
      console.error('Login error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: t('login.pleaseTryAgain'),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
    return 'success';
  };

  const register = async (
    schoolName: string,
    firstName: string, 
    lastName:string,
    username: string, 
    email: string, 
    password: string,
    ipAddress = 'unknown',
    country = 'US',
    deviceInfo = '{}',
    timezone = 'UTC',
    accountType = 'COACH',
    studentToken = '',
  ) => {
   //setIsLoading(true);
    
    try {
      const url =
       accountType === 'Student'
        ? `${BASE_URL}/api/register/student`
        : accountType === 'Teacher'
        ? `${BASE_URL}/api/register/teacher`
        : accountType === 'Parent'
        ? `${BASE_URL}/api/register/parent`
        : `${BASE_URL}/api/register`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept' : 'application/json'
        },
        body: JSON.stringify({ 
          username, 
          email, 
          firstName, 
          lastName, 
          account_type: accountType,
          password,
          ip_address: ipAddress,
          country,
          device_info: deviceInfo,
          timezone,
          provider: 'email',
          inviteToken: studentToken,
          schoolName
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status !== 0) {
        localStorage.setItem('auth-token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        // localStorage.setItem('2fa-prompt-pending', '1');

        //reset all localstorage wizard
        localStorage.removeItem('wizard-started');
        localStorage.removeItem('wizard-step');
        localStorage.removeItem('wizard-active');
        localStorage.removeItem('wizard-welcome-dismissed');
        localStorage.removeItem('wizard-completed');
        window.dispatchEvent(new Event('wizard-state-changed'));

        await fetchUserData(data.access_token);
        
        window.scrollTo(0, 0);
        navigate('/agenda', { replace: true });
        
        return { success: true, data };
      } else {
        return { 
          success: false, 
          error: data.message || 'Registration failed',
          data
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred',
        originalError: error
      };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (token: string, otpCode?: string): Promise<'success' | '2fa_required'> => {
    setIsLoading(true);
    const ipInfo = await getIpInfo();
    const deviceInfo = getDeviceInfo();
    try {
      const response = await fetch(`${BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token,
          ...(otpCode ? { code: otpCode } : {}),
          ip_address: ipInfo.ip,
          country: ipInfo.countryCode,
          device_info: JSON.stringify(deviceInfo),
        }),
      });

      const data = await response.json();


      const message = typeof data?.message === 'string' ? data.message : '';
      const requiresTwoFactor =
        data?.two_factor_required === true ||
        message === 'two_factor_required' ||
        message.includes('two_factor');

      //console.log('Login response:', { response, data, message, requiresTwoFactor });

      // Le backend demande le code 2FA avant de délivrer le token
      if (requiresTwoFactor) {
        setPendingTwoFactor(true);
        setPendingTwoFactorProvider('google');
        setPendingTwoFactorToken(token);
        return '2fa_required';
      }

      //reset all localstorage wizard
      localStorage.removeItem('wizard-started');
      localStorage.removeItem('wizard-step');
      localStorage.removeItem('wizard-active');
      localStorage.removeItem('wizard-welcome-dismissed');
      localStorage.removeItem('wizard-completed');
      window.dispatchEvent(new Event('wizard-state-changed'));


      if (!response.ok) {
        throw new Error(data.message || 'Google authentication failed');
      }

      localStorage.setItem('auth-token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);

       toast({
        title: t('login.loginSuccess'),
        description: t('login.connectedWithGoogle'),
      });

      await fetchUserData(data.access_token);

      navigate('/agenda', { replace: true });

      if(!requiresTwoFactor) {
        localStorage.setItem('2fa-prompt-pending', '1');
      }

      return 'success';

    } catch (error) {
      console.error('Google login error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogleCode = async (code: string): Promise<'success' | '2fa_required'> => {
    setIsLoading(true);
    const ipInfo = await getIpInfo();
    const deviceInfo = getDeviceInfo();
    try {
      const response = await fetch(`${BASE_URL}/api/auth/google/code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ code,
          ip_address: ipInfo.ip,
          country: ipInfo.countryCode,
          device_info: JSON.stringify(deviceInfo),
          redirect_url: `${SITE_URL}/oauth2redirect/google.html`
        }),
      });

      const data = await response.json();

      console.log('Google login data:', data);


      const message = typeof data?.message === 'string' ? data.message : '';
      const requiresTwoFactor =
        data?.two_factor_required === true ||
        message === 'two_factor_required' ||
        message.includes('two_factor');

      // Le backend demande le code 2FA avant de délivrer le token
      if (requiresTwoFactor) {
        setPendingTwoFactor(true);
        setPendingTwoFactorProvider('google');
        setPendingTwoFactorToken(data?.token);
        return '2fa_required';
      }

      if (!response.ok) {
        throw new Error(data.message || 'Google authentication failed');
      }

      localStorage.setItem('auth-token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);

      //reset all localstorage wizard
      localStorage.removeItem('wizard-started');
      localStorage.removeItem('wizard-step');
      localStorage.removeItem('wizard-active');
      localStorage.removeItem('wizard-welcome-dismissed');
      localStorage.removeItem('wizard-completed');
      window.dispatchEvent(new Event('wizard-state-changed'));

      await fetchUserData(data.access_token);

      navigate('/agenda', { replace: true });

      toast({
        title: t('login.loginSuccess'),
        description: t('login.connectedWithGoogle'),
      });

      return 'success';
    } catch (error) {
      console.error('Google login error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApple = async (token: string, otpCode?: string): Promise<'success' | '2fa_required'> => {
    setIsLoading(true);
    const ipInfo = await getIpInfo();
    const deviceInfo = getDeviceInfo();
    try {
      const response = await fetch(`${BASE_URL}/api/auth/apple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token,
          ...(otpCode ? { code: otpCode } : {}),
          ip_address: ipInfo.ip,
          country: ipInfo.countryCode,
          device_info: JSON.stringify(deviceInfo),
        }),
      });
      
      const data = await response.json();
      const message = typeof data?.message === 'string' ? data.message : '';
      const requiresTwoFactor =
        data?.two_factor_required === true ||
        message === 'two_factor_required' ||
        message.includes('two_factor');

      if (requiresTwoFactor) {
        setPendingTwoFactor(true);
        setPendingTwoFactorProvider('apple');
        setPendingTwoFactorToken(token);
        return '2fa_required';
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Apple authentication failed');
      }
      console.log('Apple login successful:', data);
      localStorage.setItem('auth-token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);

      //reset all localstorage wizard
      localStorage.removeItem('wizard-started');
      localStorage.removeItem('wizard-step');
      localStorage.removeItem('wizard-active');
      localStorage.removeItem('wizard-welcome-dismissed');
      localStorage.removeItem('wizard-completed');
      window.dispatchEvent(new Event('wizard-state-changed'));
      
      await fetchUserData(data.access_token);
      navigate('/agenda', { replace: true });
      
      toast({
        title: t('login.loginSuccess'),
        description: t('login.title'),
      });
      return 'success';
    } catch (error) {
      console.error('Apple login error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithAppleCode = async (code: string): Promise<'success' | '2fa_required'> => {
    setIsLoading(true);
    const ipInfo = await getIpInfo();
    const deviceInfo = getDeviceInfo();
    try {
      const response = await fetch(`${BASE_URL}/api/auth/apple/code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ code,
          ip_address: ipInfo.ip,
          country: ipInfo.countryCode,
          device_info: JSON.stringify(deviceInfo),
          redirect_url: `${SITE_URL}/oauth2redirect/apple.html`
        }),
      });

      const data = await response.json();

      const message = typeof data?.message === 'string' ? data.message : '';
      const requiresTwoFactor =
        data?.two_factor_required === true ||
        message === 'two_factor_required' ||
        message.includes('two_factor');

      if (requiresTwoFactor) {
        setPendingTwoFactor(true);
        setPendingTwoFactorProvider('apple');
        setPendingTwoFactorToken(token);
        return '2fa_required';
      }

      if (!response.ok) {
        throw new Error(data.message || 'Apple authentication failed');
      }

      localStorage.setItem('auth-token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);

      //reset all localstorage wizard
      localStorage.removeItem('wizard-started');
      localStorage.removeItem('wizard-step');
      localStorage.removeItem('wizard-active');
      localStorage.removeItem('wizard-welcome-dismissed');
      localStorage.removeItem('wizard-completed');
      window.dispatchEvent(new Event('wizard-state-changed'));

      await fetchUserData(data.access_token);
      navigate('/agenda', { replace: true });

      toast({
        title: t('login.loginSuccess'),
        description: t('login.title'),
      });
      return 'success';
    } catch (error) {
      console.error('Apple login error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: error instanceof Error ? error.message : 'Authentication failed',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
      try {
          const response = await fetch(`${BASE_URL}/api/logout`, {
              method: 'POST',
              headers: {
                'Accept' : 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
          });

          if (response.ok) {
              const data = await response.json();

              // Remove current account from switcher
              const currentId = user?.id || user?.userId;
              if (currentId) {
                useAccountStore.getState().removeAccount(currentId);
              }

              localStorage.removeItem('auth-token');
              localStorage.removeItem('invoices-pagination-params');
              setToken(null);
              setUser(null);

              //reset all localstorage wizard
              localStorage.removeItem('wizard-started');
              localStorage.removeItem('wizard-step');
              localStorage.removeItem('wizard-active');
              localStorage.removeItem('wizard-welcome-dismissed');
              localStorage.removeItem('wizard-completed');
              window.dispatchEvent(new Event('wizard-state-changed'));

              // Clear all caches
              eventService.clearEventsCache();
              queryClient.clear();

              setIsLoading(false);
              navigate('/login', { replace: true });
              toast({
                  title: t('login.loggout'),
                  description: t('login.loggoutSuccessDesc'),
              });
              
          } else {
              setIsLoading(false);
              console.error('Logout failed with status:', response.status);
              const errorData = await response.json();
              console.error('Error details:', errorData);

              toast({
                  title: t('login.loggoutFailed'),
                  variant: "destructive"
              });
          }
      } catch (error) {
          setIsLoading(false);
          console.error('Logout error:', error);

          toast({
              title: t('login.loggoutFailed'),
              variant: "destructive"
          });
      }
  };

  const logoutAll = async () => {
    setIsLoading(true);
      try {
          const response = await fetch(`${BASE_URL}/api/logoutAll`, {
              method: 'POST',
              headers: {
                'Accept' : 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
          });

          if (response.ok) {
              const data = await response.json();
              //console.log('Logout success:', data);

              localStorage.removeItem('auth-token');
              localStorage.removeItem('invoices-pagination-params');
              eventService.clearEventsCache();
              queryClient.clear();
              setToken(null);
              setUser(null);

              //reset all localstorage wizard
              localStorage.removeItem('wizard-started');
              localStorage.removeItem('wizard-step');
              localStorage.removeItem('wizard-active');
              localStorage.removeItem('wizard-welcome-dismissed');
              localStorage.removeItem('wizard-completed');
              window.dispatchEvent(new Event('wizard-state-changed'));

              toast({
                  title: t('login.loggout'),
                  description: t('login.loggoutSuccessDesc'),
              });

              setIsLoading(false);
              navigate('/login', { replace: true });
          } else {
              setIsLoading(false);
              console.error('Logout failed with status:', response.status);
              const errorData = await response.json();
              console.error('Error details:', errorData);

              toast({
                  title: t('login.loggoutFailed'),
                  variant: "destructive"
              });
          }
      } catch (error) {
          setIsLoading(false);
          console.error('Logout error:', error);

          toast({
              title: t('login.loggoutFailed'),
              variant: "destructive"
          });
      }
  };

  const logoutToken = async ($tokenParam: string) => {
    setIsLoading(true);
      try {
          const response = await fetch(`${BASE_URL}/api/logoutToken/${$tokenParam}`, {
              headers: {
                'Accept' : 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
          });

          if (response.ok) {
              const data = await response.json();
              setIsLoading(false);
          } else {
              setIsLoading(false);
              console.error('Logout failed with status:', response.status);
              const errorData = await response.json();
              console.error('Error details:', errorData);

              toast({
                  title: t('login.loggoutFailed'),
                  variant: "destructive"
              });
          }
      } catch (error) {
          setIsLoading(false);
          console.error('Logout error:', error);

          toast({
              title: t('login.loggoutFailed'),
              variant: "destructive"
          });
      }
  };

  const clearPendingTwoFactor = () => setPendingTwoFactor(false);
  const resetTwoFactor = () => {
    setPendingTwoFactor(false);
    setPendingTwoFactorProvider(null);
    setPendingTwoFactorToken(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!user,
      isLoading,
      pendingTwoFactor,
      pendingTwoFactorProvider,
      pendingTwoFactorToken,
      clearPendingTwoFactor,
      resetTwoFactor,
      login,
      register,
      loginWithGoogle,
      loginWithGoogleCode,
      loginWithApple,
      loginWithAppleCode,
      logout,
      logoutAll,
      logoutToken,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
